import { Box } from '@breathelife/mui';
import { Children, PropsWithChildren, ReactElement, ReactNode, RefObject, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '../../../Styles/themed-styled-components';
import { CloseButton } from '../../Button/CloseButton';
import { SkipLink } from '../../SkipLink/SkipLink';
import { SUBHEADER_HEIGHT_PX } from '../AssistedApplicationView/ProposedInsuredTabBar/Styles';

const skipToAssistedApplicationContentId = 'assistedApplicationContent';
export const HEADER_HEIGHT_PX = 72;

const HeaderWrapperContainer = styled(Box)`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[30]};
  display: flex;
  height: ${HEADER_HEIGHT_PX}px;
  justify-content: space-between;
  padding: 0px 16px;

  @media (max-width: ${(props) => props.theme.moduleBreakpoints.sm}) {
    height: fit-content;
    padding: 16px;
  }
`;

const CloseButtonContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
`;

export const ScrollContainer = styled(Box)`
  overflow: auto;
  flex: 3;
  scroll-behavior: smooth;
`;

export const ScrollContent = styled(Box)`
  display: flex;

  @media (max-width: ${(props) => props.theme.moduleBreakpoints.sm}) {
    flex-direction: column;
    padding: 15px;
  }
`;

export const ViewContainer = styled(Box)<{ $withSubheader: boolean }>`
  display: flex;
  height: calc(
    100vh - ${(props) => (props.$withSubheader ? HEADER_HEIGHT_PX + SUBHEADER_HEIGHT_PX : HEADER_HEIGHT_PX)}px
  );

  @media (max-width: ${(props) => props.theme.moduleBreakpoints.sm}) {
    flex-direction: column;
  }
`;

export const NavigationContainer = styled(Box)`
  flex: 1;
  padding: 24px;
  overflow: auto;
  border-right: 1px solid ${(props) => props.theme.colors.grey[30]};
  max-width: 20%;
  min-width: 10%;

  nav {
    width: 100%;
  }

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  @media (max-width: ${(props) => props.theme.moduleBreakpoints.sm}) {
    max-width: initial;
    border-bottom: 1px solid ${(props) => props.theme.colors.grey[30]};
  }
`;

type AssistedApplicationLayoutProps = PropsWithChildren<{
  onClose?: () => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
}>;
export function AssistedApplicationLayout({
  children,
  onClose,
  scrollContainerRef,
}: AssistedApplicationLayoutProps): ReactElement {
  let header, subheader, sidebar, scrollableContent, content;

  const { t } = useTranslation();

  Children.forEach(children, (child: ReactNode) => {
    if (isValidElement(child)) {
      if (child?.type === AssistedApplicationLayout.Header) header = child;
      if (child?.type === AssistedApplicationLayout.Sidebar) sidebar = child;
      if (child?.type === AssistedApplicationLayout.ScrollableContent) scrollableContent = child;
      if (child?.type === AssistedApplicationLayout.Content) content = child;
      if (child?.type === AssistedApplicationLayout.Subheader) subheader = child;
    }
  });

  return (
    <div>
      <HeaderWrapperContainer>
        <SkipLink href={`#${skipToAssistedApplicationContentId}`} title={t('skipLinks.header')} />
        {header}
        {!!onClose && (
          <CloseButtonContainer ml={5}>
            <CloseButton onClick={onClose} />
          </CloseButtonContainer>
        )}
      </HeaderWrapperContainer>
      {subheader}
      <Box id={skipToAssistedApplicationContentId}>
        <ViewContainer $withSubheader={false}>
          {!!sidebar && <NavigationContainer>{sidebar}</NavigationContainer>}
          {scrollableContent ? (
            <ScrollContainer ref={scrollContainerRef}>
              <ScrollContent>{scrollableContent}</ScrollContent>
            </ScrollContainer>
          ) : (
            content
          )}
        </ViewContainer>
      </Box>
    </div>
  );
}

AssistedApplicationLayout.Header = ({ children }: PropsWithChildren<{}>) => children;
AssistedApplicationLayout.Subheader = ({ children }: PropsWithChildren<{}>) => children;
AssistedApplicationLayout.Sidebar = ({ children }: PropsWithChildren<{}>) => children;
AssistedApplicationLayout.Content = ({ children }: PropsWithChildren<{}>) => children;
AssistedApplicationLayout.ScrollableContent = ({ children }: PropsWithChildren<{}>) => children;
